import config from '../../config';
import { Api, ApiConfig } from '../api';

export interface CreateCheckoutSessionRequest {
    amount: number;
    currency: string;
    payload: {
        type: string;
        params: Record<string, any>;
    };
    successUrl: string;
    cancelUrl: string;
}

export interface CreateCheckoutSessionResponse {
    sessionId: string;
    url: string;
}

// User Plan interfaces
export interface UserPlan {
    nextResetDate: string | null;
    planName: string;
    planId: string;
    endAt: string;
    startAt: string;
    status: string;
    planCode: string;
    billingCycle: 'monthly' | 'yearly';
}


// User Points interfaces
export interface UserPoints {
    user_id: string;
    total_points: number;
    current_period_points: number;
    used_points: number;
    balance_points: number;
    last_reset_time: string;
    reset_count: number;
    last_update_time: string;
    daily_free_points: number;
    daily_free_used: number;
    daily_free_balance: number;
    last_daily_reset: string;
    last_login_date: string;
    total_remaining: number;
}


// User Points History interfaces
export interface PointsHistoryItem {
    id: string;
    user_id: string;
    used_points: number;
    session_id: string;
    task_name: string;
    operation_type: string;
    response_status: string;
    execution_time_ms: number;
    ip_address: string;
    created_at: string;
}


export interface GetUserPointsHistoryParams {
    page: number;
    size: number;
}

export interface GetUserPointsHistoryResponse {
    pagination: {
        total: number;
        size: number;
        totalPages: number;
        hasPrevious: boolean;
        hasNext: boolean;
        page: number;
    },
    data: PointsHistoryItem[]
}

// Create Subscription interfaces
export interface CreateSubscriptionRequest {
    planId: string;
    successUrl: string;
    cancelUrl: string;
}

// Create Subscription Portal interfaces
export interface CreateSubscriptionPortalRequest {
    returnUrl: string;
}

export interface CreateSubscriptionPortalResponse {
    url: string;
    paymentType: string;
}

// Invoice interfaces
export interface Invoice {
    id: string;
    workspaceSubscriptionId: string;
    stripeInvoiceId: string;
    amountCents: number;
    currency: string;
    status: string;
    hostedInvoiceUrl: string;
    pdfUrl: string;
    createdAt: string;
}

export interface GetInvoicesResponse {
    pagination: {
        total: number;
        size: number;
        totalPages: number;
        hasPrevious: boolean;
        hasNext: boolean;
        page: number;
    };
    invoices: Invoice[];
}

// Plan interfaces
export interface Plan {
    id: string;
    code: string;
    name: string;
    tier: string;
    pointsQuota: number;
    priceCents: number;
    currency: string;
    billingCycle: string;
    isActive: boolean;
    isDefault: boolean;
    createdAt: string;
}

export class BillingService {
    private api: Api;

    constructor(config: ApiConfig) {
        this.api = new Api(config);
    }

    // Get user plan information
    async getUserPlans(): Promise<UserPlan> {
        const response = await this.api.get<UserPlan>('/api/user/plans');
        return {...response}
    }

    // Get user points information
    async getUserPoints(): Promise<UserPoints> {
        return this.api.get('/api/user/points');
    }

    // Get user points history
    async getUserPointsHistory(params: GetUserPointsHistoryParams): Promise<GetUserPointsHistoryResponse> {
        return this.api.get('/api/user/points/history', { params });
    }

    // Create subscription
    async createSubscription({
        planId, successUrl, cancelUrl, axiosRequestConfig
    }: {planId: string, successUrl: string, cancelUrl: string, axiosRequestConfig?: any}): Promise<any> {
        const data: CreateSubscriptionRequest = {
            planId, successUrl, cancelUrl
        };
        return this.api.post('/api/stripe/subscription/create', data, { ...axiosRequestConfig, params: data });
    }

    // Cancel subscription
    async cancelSubscription(): Promise<any> {
        return this.api.post('/api/stripe/subscription/cancel', {});
    }

    // Create subscription portal
    async createSubscriptionPortal(returnUrl: string): Promise<CreateSubscriptionPortalResponse> {
        return this.api.post('/api/stripe/subscription/portal?returnUrl=' + returnUrl);
    }

    // Get invoices
    async getInvoices(params?: { page?: number; size?: number }): Promise<GetInvoicesResponse> {
        return this.api.get('/api/stripe/invoices', { params });
    }

    // Get all available plans
    async getPlans(): Promise<Plan[]> {
        return this.api.get('/api/plans');
    }
}

export const billingService = new BillingService({
    baseURL: config.services.billing.apiHost,
    headers: {'Content-Type': 'application/json'}
});
